// client/src/context/ActingStoreContext.js
// The super-admin's store switcher: which store's data the admin screens are
// currently acting on. Distinct from StoreContext (the PUBLIC branding of
// whichever domain the visitor is actually on) - this only matters for a
// logged-in super_admin managing multiple stores from one admin session, so
// it lives under AuthProvider (needs to know who's logged in), not above it.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { API_BASE_URL } from '../utils/media';

const ACTING_STORE_KEY = 'actingStoreId';

export const ActingStoreContext = createContext({
  actingStoreId: null,
  setActingStoreId: () => {},
  managedStores: [],
  refreshManagedStores: () => {},
});

export const ActingStoreProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';

  const [actingStoreId, setActingStoreIdState] = useState(() => {
    const stored = localStorage.getItem(ACTING_STORE_KEY);
    return stored ? Number(stored) : null;
  });
  const [managedStores, setManagedStores] = useState([]);

  const setActingStoreId = (id) => {
    setActingStoreIdState(id);
    if (id) localStorage.setItem(ACTING_STORE_KEY, String(id));
    else localStorage.removeItem(ACTING_STORE_KEY);
  };

  const refreshManagedStores = useCallback(async () => {
    if (!isSuperAdmin) {
      setManagedStores([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/stores`);
      setManagedStores(response.data);
      // Default to the first managed store if none selected yet (or the
      // previously-selected one no longer exists).
      setActingStoreIdState((current) => {
        if (current && response.data.some((s) => s.id === current)) return current;
        const fallback = response.data[0]?.id || null;
        if (fallback) localStorage.setItem(ACTING_STORE_KEY, String(fallback));
        return fallback;
      });
    } catch (error) {
      console.error('Failed to fetch managed stores', error);
    }
  }, [isSuperAdmin]);

  useEffect(() => { refreshManagedStores(); }, [refreshManagedStores]);

  // Attach X-Acting-Store-Id to every request only for a super_admin - a
  // regular admin's acting store comes purely from their own JWT server-side
  // and this header is ignored for them anyway, but keep it unset by default
  // so nothing looks like it's trying to spoof a store.
  //
  // The admin pages call the API two different ways (some via axios, most via
  // plain fetch()) - patch both so the switcher works everywhere without
  // having to touch every individual page's request code.
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (isSuperAdmin && actingStoreId) {
        config.headers['X-Acting-Store-Id'] = actingStoreId;
      } else {
        delete config.headers['X-Acting-Store-Id'];
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [isSuperAdmin, actingStoreId]);

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = (input, init = {}) => {
      if (isSuperAdmin && actingStoreId) {
        const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
        headers.set('X-Acting-Store-Id', actingStoreId);
        return originalFetch(input, { ...init, headers });
      }
      return originalFetch(input, init);
    };
    return () => { window.fetch = originalFetch; };
  }, [isSuperAdmin, actingStoreId]);

  return (
    <ActingStoreContext.Provider value={{ actingStoreId, setActingStoreId, managedStores, refreshManagedStores }}>
      {children}
    </ActingStoreContext.Provider>
  );
};

export const useActingStore = () => useContext(ActingStoreContext);
